import { useEffect, useState } from 'react';
import { useLocalization } from '@/hooks/use-localization';
import { CountryType, CustomerType, FieldValuesType } from '../types';
import { Steps } from '../components/steps/step-layout';
import { useSearchParams } from 'react-router-dom';

export const SESSION_STORAGE_KEY = 'businessFormValues';

export const useValues = (fetchProductInformation = false) => {
    const [searchParams] = useSearchParams();
    const { getCountry, getLanguage } = useLocalization();

    const getLanguageCode = () => {
        const lang = getLanguage();
        return lang === 'no' ? 'nb' : lang;
    };

    const getProductCode = () => {
        if (!fetchProductInformation) {
            return 'X0082083';
        }

        if (searchParams.has('productCode')) {
            return searchParams.get('productCode') as string;
        }

        return '';
    };

    const getInitialValues = (): FieldValuesType => {
        return {
            countryCode: getCountry().toLocaleUpperCase() as CountryType,
            languageCode: getLanguageCode(),
            // This should not be chosen when the customer can select a product themselves, otherwise in the old flow revert to default product
            productCode: getProductCode(),
            productType: 'DEPOSIT',
            company: {
                governmentId: {
                    id: '',
                    customerType: CustomerType.LEGAL,
                    countryCode: getCountry().toLocaleUpperCase() as CountryType,
                },
                organizationName: '',
                organizationType: '',
                email: '',
                phoneNumber: '',
                address: {
                    street: '',
                    city: '',
                    zipCode: '',
                    countryCode: getCountry().toLocaleUpperCase() as CountryType,
                },
            },
            applicant: {
                name: {
                    first: '',
                    last: '',
                },
                governmentId: {
                    id: '',
                    customerType: CustomerType.NATURAL,
                    countryCode: getCountry().toLocaleUpperCase() as CountryType,
                },
                email: '',
                phoneNumber: '',
            },
            payoutAccount: {
                accountHolderBank: '',
                clearingNumber: '',
                accountNumber: '',
            },
            // Verklig huvudman. NB We have to set this data from the KYC values
            // so it's not updated in the application, rather we get it by checking
            // against the KYC 'OWNERSHIP_AND_CONTROL' fields
            ownerships: [],
            // Nyckelpersoner
            keyRoles: [],
            allKeyRoles: [],
            // Undertecknare
            signatoryCombinations: [],
        };
    };

    const getValuesFromStorage = () => {
        // SSR-safe: sessionStorage doesn't exist on the server. On the
        // server we return null and let the lazy initializer fall back to
        // the fresh initial values; the client picks up the stored values
        // on the first render (before any effects run).
        if (typeof window === 'undefined') {
            return null;
        }
        const session = sessionStorage?.getItem(SESSION_STORAGE_KEY);
        if (!session || typeof session !== 'string') {
            return null;
        }

        return JSON.parse(session) ?? null;
    };

    const [step, setStep] = useState<Steps>(0);
    // Rehydrate from sessionStorage SYNCHRONOUSLY via the lazy initializer
    // rather than in a mount effect. A previous `useEffect(_, [])` based
    // rehydration raced with `Intro`'s govid-seeding effect: child effects
    // (Intro) run BEFORE parent effects (this hook), so the parent's
    // rehydration would overwrite Intro's freshly-set
    // `applicant.governmentId.id` back to the empty value from storage —
    // leaving the user with an empty form after BankID login. With the
    // lazy initializer the storage read happens during the initial render,
    // before any effects fire, so seeding always wins.
    const [values, setValues] = useState<FieldValuesType>(() => getValuesFromStorage() ?? getInitialValues());
    // Unused since 2026-11: only the förening block set this, see intro.tsx.
    const [manualApplicationOnly, setManualApplicationOnly] = useState(false);

    useEffect(() => {
        // Save to session storage every time values change
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(values));
    }, [values]);

    return {
        values,
        setValues,
        step,
        setStep,
        getValuesFromStorage,
        manualApplicationOnly,
        setManualApplicationOnly,
    };
};
